import { useDispatch, useSelector } from "react-redux";
import { CommonLayout } from "../commonComponents";
import { RootStateType } from "../../redux-store/reducers";
import { isModuleAllowed } from "../../utils/utilityFunctions";
import { useHistory } from "react-router";
import { Badge, Box, Button, Drawer, IconButton, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import BreadCrum from "../../lib/BreadCrum";
import { useEffect, useRef, useState } from "react";
import { SearchIcon } from "../customSVGs";
import { BlackExitIcon, DialogCloseIcon, FilterIcon } from "../../assets/SvgIcons";
import { FilterCheckbox } from "../investorOverview/common/CustomFilter";
import KycDetailsTable from "./kycDetailsTable";
import { PaginationObj } from "../investorOverview/types";
import { getMfuKycStatusList } from "../../redux-store/actions/onBoarding";
import { GetMfuKycStatusListResponseBody, MfuKycStatusObj } from "../../redux-store/types/api-types";

interface FilterObj {
    statusId: string;
    statusName: string;
    checked?: boolean;
}

const STATUS_FILTERS: Array<FilterObj> = [
    {
        statusId: "Pending",
        statusName: "Pending"
    },
    {
        statusId: "Failed",
        statusName: "Failed"
    },
    {
        statusId: "Completed",
        statusName: "Completed"
    }
]

const DEBOUNCE_DELAY = 300

export default function KycApplications(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // State
    const [loadingStatus, setLoadingStatus] = useState<"initialLoading" | "tableDataLoading" | "">("")
    const [searchText, setSearchText] = useState<string>("")
    const [statusFilterSearchText, setStatusFilterSearchText] = useState<string>("")
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const [statusFilterData, setStatusFilterData] = useState<Array<FilterObj>>([])
    const [appliedFilterData, setAppliedFilterData] = useState<Array<FilterObj>>([{ statusId: "Pending", statusName: "Pending", checked: true }])
    const [tableData, setTableData] = useState<MfuKycStatusObj[]>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
    })

    // Prevents the debounced search useEffect from firing on initial mount. On mount, searchText is "" and the effect runs, but this ref being false ensures we skip it (the initial useEffect already fetches data). Only set to true when the user actually interacts with the search input.
    const isSearchDirtyRef = useRef<boolean>(false)

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'KYC Applications',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const fetchStatusFilters = () => {
        setStatusFilterData(STATUS_FILTERS.map((obj: FilterObj) => ({ ...obj, checked: obj.statusId === "Pending" })))
    }

    const fetchTableData = async (
        payload: {
            page: number;
            rowsPerPage: number;
            search: string;
            filters: string[]
        },
        cancelledRef?: { current: boolean }
    ) => {
        const apiPayload = {
            searchInput: payload?.search ?? "",
            kraKYCStatus: (payload?.filters ?? []).length ? payload.filters : "",
            currentPage: payload.page + 1,
            rowsPerPage: payload.rowsPerPage,
        }

        const response = await (dispatch(getMfuKycStatusList(apiPayload)) as unknown as Promise<GetMfuKycStatusListResponseBody>);

        if (cancelledRef?.current) return

        setTableData(response?.data ?? [])
        setTablePaginationObj({
            currentPage: payload.page,
            rowsPerPage: payload.rowsPerPage,
            totalRows: response.count,
        })
    }

    const fetchInitialData = async () => {
        try {
            setLoadingStatus("initialLoading")

            await Promise.all([
                fetchStatusFilters(),
                fetchTableData({
                    page: 0,
                    rowsPerPage: tablePaginationObj.rowsPerPage,
                    search: "",
                    filters: ["Pending"],
                }),
            ])
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    useEffect(() => {
        fetchInitialData()
    }, [])

    useEffect(() => {
        if (!isSearchDirtyRef.current) {
            return
        }

        let cancelled = false
        const cancelledRef = { current: false }

        const debounceTimer = setTimeout(async () => {
            try {
                setLoadingStatus("tableDataLoading")

                await fetchTableData({
                    page: 0,
                    rowsPerPage: tablePaginationObj.rowsPerPage,
                    search: searchText,
                    filters: appliedFilterData.map((obj: FilterObj) => obj.statusId),
                }, cancelledRef)
            } catch (error) {
                console.log("Error:", error)
            } finally {
                if (!cancelled) {
                    setLoadingStatus("")
                }
            }
        }, DEBOUNCE_DELAY)

        return () => {
            clearTimeout(debounceTimer)
            cancelled = true
            cancelledRef.current = true
        }
    }, [searchText])

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        isSearchDirtyRef.current = true
        e.preventDefault()
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value)
        isSearchDirtyRef.current = true
    }

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true);
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
        setStatusFilterData((prev: Array<FilterObj>) =>
            prev.map((item: FilterObj) => ({
                ...item,
                checked: appliedFilterData.some((obj: FilterObj) => obj.statusId === item.statusId)
            }))
        )
        setStatusFilterSearchText("")
    }

    const handleClearAllFilters = async () => {
        try {
            setLoadingStatus("tableDataLoading")

            setFilterDrawerOpen(false)

            setStatusFilterData((prev: Array<FilterObj>) => prev.map((obj: FilterObj) => ({ ...obj, checked: false })))

            setAppliedFilterData([])

            await fetchTableData({
                page: 0,
                rowsPerPage: tablePaginationObj.rowsPerPage,
                search: searchText,
                filters: [],
            })
        } catch (error) {
            console.log("Error:", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleApplyFilters = async () => {
        try {
            setLoadingStatus("tableDataLoading")

            setFilterDrawerOpen(false)

            const newAppliedFilters = statusFilterData.filter((item: FilterObj) => item.checked)

            setAppliedFilterData(newAppliedFilters)

            await fetchTableData({
                page: 0,
                rowsPerPage: tablePaginationObj.rowsPerPage,
                search: searchText,
                filters: newAppliedFilters.map((obj: FilterObj) => obj.statusId),
            })
        } catch (error) {
            console.log("Error:", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleClearStatusFilterSearch = () => {
        setStatusFilterSearchText("")
    }

    const handleStatusFilterSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStatusFilterSearchText(event.target.value);
    }

    const handleStatusFilterChange = (value: string, checked: boolean) => {
        const updatedStatusFilterData = statusFilterData.map((item: FilterObj) => (item.statusId === value) ? { ...item, checked } : item);

        setStatusFilterData(updatedStatusFilterData)
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            setLoadingStatus("tableDataLoading")

            await fetchTableData({
                page,
                rowsPerPage: tablePaginationObj.rowsPerPage,
                search: searchText,
                filters: appliedFilterData.map((obj: FilterObj) => obj.statusId),
            })
        } catch (error) {
            console.log("Error:", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setLoadingStatus("tableDataLoading")

            const newRowsPerPage = parseInt(event.target.value, 10)

            await fetchTableData({
                page: 0,
                rowsPerPage: newRowsPerPage,
                search: searchText,
                filters: appliedFilterData.map((obj: FilterObj) => obj.statusId),
            })
        } catch (error) {
            console.log("Error:", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const selectedStatusFilters = statusFilterData.filter((item: FilterObj) => item.checked)
    const filteredStatusFilters = statusFilterData.filter((obj: FilterObj) =>
        `${obj?.statusName ?? ""}`.toLowerCase().includes(`${statusFilterSearchText ?? ""}`.toLowerCase())
    )

    return (
        <CommonLayout>
            <>
                <Box sx={{ marginBottom: "16px" }}>
                    <BreadCrum array={routerPath} />
                </Box>
                <Typography
                    variant="h1"
                    sx={{
                        color: theme.palette.text.primary,
                        marginBottom: "25px"
                    }}
                >
                    KYC Details
                </Typography>
                <Box
                    sx={{
                        borderRadius: "25px",
                        padding: "25px 30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '30px'
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0
                            }}
                        >
                            <TextField
                                fullWidth
                                inputProps={{
                                    'aria-describedby': 'search-kyc-helper',
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{
                                                    display: searchText.length > 0 ? 'inline-flex' : 'none',
                                                }}
                                                onMouseDown={handleClearSearch}
                                                aria-label="Clear Search"
                                            >
                                                <DialogCloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '10px',
                                        height: '50px',
                                        border: `1px solid ${theme.palette.text.secondary}B2`,
                                        '& fieldset': {
                                            border: 'none'
                                        },
                                        '& input': {
                                            ...theme.typography.body_500,
                                            paddingLeft: '10px',
                                            color: theme.palette.text.primary,
                                            '::placeholder': {
                                                ...theme.typography.subtitle_400,
                                                color: theme.palette.text.secondary,
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                                placeholder="Search by Name, PAN or Reference ID"
                                value={searchText}
                                onChange={handleSearchTextChange}
                            />
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <Button
                                variant="text"
                                disabled={["initialLoading", "tableDataLoading"].includes(loadingStatus)}
                                startIcon={
                                    <Badge
                                        showZero
                                        badgeContent={(appliedFilterData ?? []).length}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                right: 0,
                                                top: -3,
                                                backgroundColor: ["initialLoading", "tableDataLoading"].includes(loadingStatus)
                                                    ? theme.palette.grey[400]
                                                    : theme.palette.primary[500],
                                                color: theme.palette.common.white,
                                                ...theme.typography.subtitle_600
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', opacity: ["initialLoading", "tableDataLoading"].includes(loadingStatus) ? 0.4 : 1 }}>
                                            <FilterIcon />
                                        </Box>
                                    </Badge>
                                }
                                sx={{
                                    ...theme.typography.body_500,
                                    color: theme.palette.text.primary,
                                    padding: "10px",
                                    whiteSpace: 'nowrap',
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[50],
                                        padding: "10px"
                                    },
                                    "&.Mui-disabled": {
                                        color: theme.palette.text.secondary
                                    }
                                }}
                                TouchRippleProps={{
                                    style: {
                                        color: theme.palette.grey[300]
                                    }
                                }}
                                onClick={handleOpenFilterDrawer}
                            >
                                Filters
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ marginTop: '20px' }}>
                        <KycDetailsTable
                            isLoading={["initialLoading", "tableDataLoading"].includes(loadingStatus)}
                            tableData={tableData}
                            totalCount={tablePaginationObj.totalRows}
                            currentPage={tablePaginationObj.currentPage}
                            rowsPerPage={tablePaginationObj.rowsPerPage}
                            onPageChange={handleTablePageChange}
                            onRowsPerPageChange={handleTableRowsPerPageChange}
                        />
                    </Box>
                </Box>
                <Drawer
                    open={isFilterDrawerOpen}
                    onClose={handleCloseFilterDrawer}
                    anchor="right"
                    PaperProps={{
                        sx: {
                            width: "450px",
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }
                    }}
                    ModalProps={{
                        sx: {
                            backgroundColor: `${theme.palette.common.black}03`
                        }
                    }}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            padding: "25px 0 0 25px",
                            backgroundColor: theme.palette.common.white,
                            zIndex: 1
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingBottom: '20px',
                                paddingRight: '25px',
                                borderBottomWidth: '0.7px',
                                borderBottomStyle: 'solid',
                                borderBottomColor: `${theme.palette.text.secondary}B2`,
                            }}>
                            <Typography
                                id="filter-drawer-title"
                                variant="h2"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                Filters
                            </Typography>
                            <Box sx={{ display: "flex", columnGap: "10px" }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        ...theme.typography.body_500,

                                        color: theme.palette.textDark.primary, backgroundColor: theme.palette.primary[500],
                                        padding: "5px"
                                    }}
                                    onClick={handleApplyFilters}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="text"
                                    sx={{
                                        ...theme.typography.body_500,
                                        color: theme.palette.primary[500], padding: "5px"
                                    }}
                                    onClick={handleClearAllFilters}
                                >
                                    Clear All
                                </Button>
                                <IconButton
                                    aria-label="close"
                                    onClick={handleCloseFilterDrawer}
                                    sx={{ padding: "5px" }}
                                >
                                    <BlackExitIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Typography
                            variant="h3"
                            sx={{
                                color: theme.palette.text.primary,
                                marginTop: "30px",
                                marginBottom: "20px",
                                paddingRight: '25px'
                            }}
                        >
                            Status
                        </Typography>
                        <Box sx={{ paddingRight: '25px' }}>
                            <TextField
                                fullWidth
                                id='status-search'
                                inputProps={{ 'aria-label': 'Search Status' }}
                                InputProps={{
                                    startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{ display: statusFilterSearchText.length > 0 ? 'inline-flex' : 'none' }}
                                                onClick={handleClearStatusFilterSearch}
                                                aria-label="Clear Search"
                                            >
                                                <DialogCloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '57px',
                                        height: '47.17px',
                                        border: `1px solid ${theme.palette.text.secondary}B2`,
                                        '& fieldset': { border: 'none' },
                                        '& input': {
                                            paddingLeft: '10px',
                                            ...theme.typography.body_500,
                                            color: theme.palette.text.primary,
                                            '::placeholder': { ...theme.typography.subtitle_400, color: theme.palette.text.secondary, opacity: 1 },
                                        },
                                    },
                                }}
                                placeholder="Search..."
                                value={statusFilterSearchText}
                                onChange={handleStatusFilterSearchTextChange}
                            />
                        </Box>
                        {(selectedStatusFilters.length > 0) && (
                            <Box
                                sx={{
                                    maxHeight: '215px',
                                    overflow: 'auto',
                                    marginTop: '25px',
                                    paddingBottom: '25px',
                                    borderBottomWidth: "0.7px",
                                    borderBottomStyle: 'solid',
                                    borderBottomColor: `${theme.palette.text.secondary}B2`,
                                }}>
                                {selectedStatusFilters.map((filterObj: FilterObj) =>
                                    <FilterCheckbox
                                        key={filterObj.statusId}
                                        value={filterObj.statusId}
                                        label={filterObj.statusName}
                                        checked={!!filterObj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            handleStatusFilterChange(filterObj.statusId, event.target.checked)
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: "25px 0 25px 25px"
                        }}
                    >
                        <Box>
                            {(filteredStatusFilters.length > 0) ? (
                                filteredStatusFilters.map((filterObj: FilterObj) => (
                                    <FilterCheckbox
                                        key={filterObj.statusId}
                                        value={filterObj.statusId}
                                        label={filterObj.statusName}
                                        checked={!!filterObj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            handleStatusFilterChange(filterObj.statusId, event.target.checked)
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        textAlign: 'center',
                                        marginTop: '20px',
                                        fontWeight: 600
                                    }}
                                >
                                    No Data
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Drawer>
            </>
        </CommonLayout >
    )
}
